import React, { useEffect, useState, useRef } from "react";
import { api, apiError, mediaUrl } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LifeBuoy, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };
const STATUS_COLOR = { open: "#E19C00", in_progress: "#520202", resolved: "#2e7d32" };

export default function Help() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mine, setMine] = useState([]);
  const fileRef = useRef();

  const load = async () => { const { data } = await api.get("/complaints/mine"); setMine(data); };
  useEffect(() => { load(); }, []);

  const pickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setImage(data.url); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!subject.trim() || !body.trim()) return;
    try { await api.post("/complaints", { subject, body, image_url: image }); setSubject(""); setBody(""); setImage(null); load(); toast.success("Sent to admin"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="font-display text-3xl font-bold text-spotter-maroon mb-1 flex items-center gap-2"><LifeBuoy className="w-7 h-7" />Contact Admin</h2>
      <p className="text-spotter-maroon mb-5">Your direct line to the team. Report problems or send feedback.</p>

      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-3 brand-shadow-sm">
        <div><Label>Subject</Label><Input data-testid="complaint-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="border-2 border-black" /></div>
        <div><Label>Message</Label><Textarea data-testid="complaint-body" value={body} onChange={(e) => setBody(e.target.value)} className="border-2 border-black min-h-[100px]" /></div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickImage} data-testid="complaint-file-input" />
          {image ? (
            <div className="relative inline-block">
              <img src={mediaUrl(image)} alt="screenshot" className="max-h-48 rounded-lg border-2 border-black" />
              <button data-testid="complaint-remove-image" onClick={() => setImage(null)} className="absolute top-2 right-2 bg-black text-white rounded-full p-1"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <Button type="button" variant="outline" data-testid="complaint-add-image" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-2 border-black gap-2 bg-white">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-spotter-gold" />}Attach a screenshot
            </Button>
          )}
        </div>
        <Button data-testid="complaint-submit" onClick={submit} className="bg-spotter-maroon text-spotter-cream border-2 border-black font-heading font-bold">Send to Admin</Button>
      </div>

      <h3 className="font-display text-2xl font-bold text-spotter-maroon mt-8 mb-3">Your Messages</h3>
      {mine.length === 0 ? <p className="text-spotter-maroon">You haven't contacted the admin yet.</p> : (
        <div className="space-y-3">
          {mine.map((c) => (
            <div key={c.id} className="bg-white border-2 border-black rounded-xl p-4" data-testid={`my-complaint-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-bold">{c.subject}</div>
                <span className="text-xs font-bold px-2 py-1 rounded-full border-2 border-black text-white" style={{ background: STATUS_COLOR[c.status] }}>{STATUS[c.status]}</span>
              </div>
              <p className="text-sm mt-1 whitespace-pre-wrap">{c.body}</p>
              {c.image_url && <img src={mediaUrl(c.image_url)} alt="attachment" className="mt-2 max-h-48 rounded-lg border-2 border-black" />}
              {c.admin_reply && (
                <div className="mt-3 bg-spotter-cream border-l-4 border-spotter-gold p-2 rounded">
                  <div className="text-xs font-bold text-spotter-maroon">Admin reply</div>
                  <div className="text-sm whitespace-pre-wrap">{c.admin_reply}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
